/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Dealer/role detection from access-token claims (matches backend `UserRoleResolver`). */

export type TokenClaims = Record<string, unknown>;

/** Default: no claims (unauthenticated / test) -> treated as a non-dealer with no roles. */
let claimsProvider: () => TokenClaims = () => ({});

/** Claims decoded from the active access token (the source of truth; see setAccessTokenClaims). */
let accessTokenClaims: TokenClaims = {};

/** Register a supplemental claims source (e.g. mock auth); overridden by {@link setAccessTokenClaims}. */
export function setTokenClaimsProvider(provider: () => TokenClaims): void {
  claimsProvider = provider;
}

/** Decode a JWT payload into claims; returns `{}` when missing or malformed. */
export function decodeJwtClaims(token: string | null | undefined): TokenClaims {
  if (!token) return {};
  const segment = token.split('.')[1];
  if (!segment) return {};
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return parsed !== null && typeof parsed === 'object' ? (parsed as TokenClaims) : {};
  } catch {
    return {};
  }
}

/** Record claims from the access token (authoritative over the ID token and supplemental provider). */
export function setAccessTokenClaims(token: string | null | undefined): void {
  accessTokenClaims = decodeJwtClaims(token);
}

function claims(): TokenClaims {
  let base: TokenClaims;
  try {
    base = claimsProvider() ?? {};
  } catch {
    base = {};
  }
  // Access-token claims win: they are what the backend actually authorizes on.
  return { ...base, ...accessTokenClaims };
}

/** Dealer partner type from the token (`gmVSSMPartnerType`), when present. */
export function getPartnerType(): string | undefined {
  const value = claims()['gmVSSMPartnerType'];
  return typeof value === 'string' ? value : undefined;
}

/** Dealer partner id from the token (`gmVSSMUsersPartnerID`), when present. */
export function getPartnerId(): string | undefined {
  const value = claims()['gmVSSMUsersPartnerID'];
  return typeof value === 'string' ? value : undefined;
}

/** Roles claim, normalized to a string array (accepts an array or a comma-delimited string). */
export function getRoles(): string[] {
  const raw = claims()['roles'];
  if (Array.isArray(raw)) return raw.filter((r): r is string => typeof r === 'string');
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
  }
  return [];
}

/** Older JWT role for external dealer partners (superseded by `ediag_gm_dealers`). */
export const DEALER_ROLE = 'external_dealers';

/** Current dealer group; GM dealer team members with eDiagnostics access for CCA. */
export const GM_DEALER_ROLE = 'ediag_gm_dealers';

/**
 * Broad dealer-network group. GM staff are often in it alongside an internal group, so it only
 * marks a true dealer account when no internal group is present (see `hasInternalRole`).
 */
export const DEALER_ALL_USERS_ROLE = 'Dealer_All_Users';

/** eDiag roles that grant access; used to positively detect a genuine "no access" token. */
const VALID_EDIAG_ROLES = new Set([
  'ediag_read',
  'ediag_write',
  'ediag_admin',
  'ediag_gm_service',
  'ediag_dev',
  DEALER_ROLE,
  GM_DEALER_ROLE,
  DEALER_ALL_USERS_ROLE,
  'ediag_analytics_dealer',
  'ediag_analytics_dealers',
  'ediag_analytics_service',
]);

/** True when the token carries any dealer signal (dealer group or partner claims); use hasInternalRole() to disambiguate GM staff who also carry dealer-network roles. */
export function isDealerFromClaims(): boolean {
  const roles = getRoles();
  if (
    roles.includes(DEALER_ROLE) ||
    roles.includes(GM_DEALER_ROLE) ||
    roles.includes(DEALER_ALL_USERS_ROLE)
  ) {
    return true;
  }
  const partnerType = getPartnerType()?.trim();
  if (!partnerType) return false;
  if (partnerType.toUpperCase() === 'DEALER') return true;
  return Boolean(getPartnerId()?.trim());
}

/** True only when the token explicitly lists roles but none grant eDiag access; ambiguous tokens fail open. */
export function hasNoEdiagAccess(): boolean {
  if (isDealerFromClaims()) return false;
  const roles = getRoles();
  if (roles.length === 0) return false;
  return !roles.some((r) => VALID_EDIAG_ROLES.has(r));
}
